import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Map } from 'immutable';

import { isResearcher } from 'src/utils/checkPermissions';
import { CompanyBuyerCEO } from '../../../../components/Company/CompanyBuyer/CompanyBuyerCEO';
import { PrettyErrorProvider } from '../../../../contexts/PrettyErrorContext';
import { buildSortByParam } from '../../../../helpers/paramBuilder';
import {
  getActiveProjects,
  getCurrentExecutiveLeads,
  updateCEO,
  createCEOProject,
  handleUpdateCompany,
  saveInfo,
  saveCEOProject,
  handleGetHarvcoLeadSuggestion,
  handleGetClientLeadSuggestion,
} from '../../../../actions/company/buyer/ceo';
import connectOptions, { mergeProps } from '../../../../utils/connectOptions';
import { getChanged, isDeepChanged, toJS } from '../../../../utils/ChangeSpy';
import { usePopupContext } from '../../../../contexts/PopupContext';
import { useEntityEventContext } from '../../../../contexts/EntityEventContext';

import style from './style.module.css';

const { func } = PropTypes;

const CompanyBuyerCEOContainer = props => {
  const {
    match,
    ceo,
    user,
    inputErrors,
    isDuplicateCompany,
    handleUpdateCompanyInfo,
    closeValidationError,
    getActiveProjects,
    getCurrentExecutiveLeads,
    updateCEO,
    createCEOProject,
    handleUpdateCompany,
    saveInfo,
    saveCEOProject,
    handleGetHarvcoLeadSuggestion,
    handleGetClientLeadSuggestion,
  } = props;

  const { openPopup } = usePopupContext();
  const { addOnSaveCallback, removeOnSaveCallback } = useEntityEventContext();

  const { companyId } = match.params;
  const canEditData = !isDuplicateCompany;
  const isResearcherUser = isResearcher(user.getIn(['roles', 0, 'slug'], null));

  const ceoRef = useRef(ceo);
  const companyIdRef = useRef(companyId);
  const canEditDataRef = useRef(canEditData);

  ceoRef.current = ceo;
  companyIdRef.current = companyId;
  canEditDataRef.current = canEditData;

  const handleSaveActiveProject = useCallback(() => {
    const base = ceoRef.current.getIn(['activeProjects', 'queryResults']);

    if (!isDeepChanged(base)) return;

    const body = toJS(base);

    saveCEOProject({
      companyId: companyIdRef.current,
      body,
    });
  }, [saveCEOProject]);

  const handleSubmit = useCallback(
    event => {
      if (!canEditDataRef.current) {
        return;
      }

      event.preventDefault();
      if (ceoRef.current.get('isValid')) {
        const body = getChanged(ceoRef.current);

        if (Object.keys(body).length) {
          saveInfo({ companyId: companyIdRef.current, body });
        }
      }
      // Wait untill other actions done
      setTimeout(() => {
        handleSaveActiveProject();
      }, 1);
    },
    [saveInfo, handleSaveActiveProject],
  );

  useEffect(() => {
    getCurrentExecutiveLeads({ companyId });
    getActiveProjects({ companyId });
    addOnSaveCallback(handleSubmit);

    return () => {
      removeOnSaveCallback(handleSubmit);
    };
  }, []);

  const previousCurrentExecutiveLeadsRef = useRef(ceo.get('currentExecutiveLeads'));
  const previousActiveProjectsRef = useRef(ceo.get('activeProjects'));
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousCurrentExecutiveLeadsRef.current = ceo.get('currentExecutiveLeads');
      previousActiveProjectsRef.current = ceo.get('activeProjects');

      return;
    }

    const currentExecutiveLeads = ceo.get('currentExecutiveLeads');
    const oldCurrentExecutiveLeads = previousCurrentExecutiveLeadsRef.current;
    const activeProjects = ceo.get('activeProjects');
    const oldActiveProjects = previousActiveProjectsRef.current;

    if (oldCurrentExecutiveLeads !== currentExecutiveLeads) {
      const oldSortBy = buildSortByParam(oldCurrentExecutiveLeads);
      const sortBy = buildSortByParam(currentExecutiveLeads);

      if (sortBy !== oldSortBy) {
        getCurrentExecutiveLeads({
          companyId,
          page: 1,
          sortBy,
        });
      }
    }

    if (oldActiveProjects !== activeProjects) {
      const oldSortBy = buildSortByParam(oldActiveProjects);
      const sortBy = buildSortByParam(activeProjects);

      if (sortBy !== oldSortBy) {
        getActiveProjects({
          companyId,
          page: 1,
          sortBy,
        });
      }
    }

    previousCurrentExecutiveLeadsRef.current = currentExecutiveLeads;
    previousActiveProjectsRef.current = activeProjects;
  }, [ceo, companyId, getCurrentExecutiveLeads, getActiveProjects]);

  const handleGetNextPageDataForCurrentExecutiveLeads = useCallback(
    page => {
      const currentExecutiveLeads = ceo.get('currentExecutiveLeads');
      const sortBy = buildSortByParam(currentExecutiveLeads);

      getCurrentExecutiveLeads({
        companyId,
        page,
        sortBy,
      });
    },
    [ceo, companyId, getCurrentExecutiveLeads],
  );

  const handleGetNextPageDataForActiveProjects = useCallback(
    page => {
      const activeProjects = ceo.get('activeProjects');
      const sortBy = buildSortByParam(activeProjects);

      getActiveProjects({
        companyId,
        page,
        sortBy,
      });
    },
    [ceo, companyId, getActiveProjects],
  );

  const handleDoubleClickRow = useCallback(
    (row, type) => {
      if (!canEditData) {
        return;
      }

      if (type === 'executive') {
        window.open(`/contact/${row.data.execId}/executive`, '_blank');
      }

      if (type === 'eproject') {
        window.open(`/eproject/${row.data.eprojectId}`, '_blank');
      }
    },
    [canEditData],
  );

  const handleCurrentExecutiveLeadsChange = useCallback(
    data => {
      updateCEO({
        filterName: 'currentExecutiveLeads',
        filterData: {
          [data.filterName]: data.filterData,
        },
      });
    },
    [updateCEO],
  );

  const handleActiveProjectsChange = useCallback(
    data => {
      updateCEO({
        filterName: 'activeProjects',
        filterData: {
          [data.filterName]: data.filterData,
        },
      });
    },
    [updateCEO],
  );

  const handleClientLeadSuggestionFetch = useCallback(
    ({ value }) => {
      handleGetClientLeadSuggestion(companyId, value);
    },
    [companyId, handleGetClientLeadSuggestion],
  );

  const handleGetSuggestion = useCallback(
    ({ currentCol, value, data }) => {
      if (!canEditData) {
        return;
      }

      if (/harvco/i.test(currentCol)) {
        handleGetHarvcoLeadSuggestion({ value, data });
      } else {
        handleClientLeadSuggestionFetch({ value, data });
      }
    },
    [canEditData, handleGetHarvcoLeadSuggestion, handleClientLeadSuggestionFetch],
  );

  const handleCreateNewCEOProject = useCallback(
    (formData, callback) => {
      let queryResults = toJS(ceo.getIn(['activeProjects', 'queryResults']));

      queryResults = queryResults.map(item => {
        if (item.clientLeadId === '') {
          delete item.clientLeadId;
        }

        if (item.harvcoLeadId === '') {
          delete item.harvcoLeadId;
        }

        return item;
      });

      const body = {
        active: true,
        category: formData.get('category'),
      };

      if (formData.getIn(['harvcoLead', 'selected'])) {
        body.harvco_lead_id = formData.getIn(['harvcoLead', 'selected', 'id']);
      }

      if (formData.getIn(['clientLead', 'selected'])) {
        body.client_lead_id = formData.getIn(['clientLead', 'selected', 'id']);
      }
      queryResults.push(body);

      createCEOProject({
        company_id: companyId,
        body: {
          data: queryResults,
        },
        callback,
      });
    },
    [ceo, companyId, createCEOProject],
  );

  const handleAddCEOProject = useCallback(() => {
    if (!canEditData) {
      return;
    }

    openPopup('AddNewCEOProject', {
      buyerId: companyId,
      onCreate: handleCreateNewCEOProject,
      onSuccessResponse: () => {
        handleGetNextPageDataForActiveProjects(1);
      },
    });
  }, [canEditData, companyId, openPopup, handleCreateNewCEOProject, handleGetNextPageDataForActiveProjects]);

  const handleChange = useCallback(
    ({ name, value }) => {
      if (!canEditData) {
        return;
      }

      handleUpdateCompany({ name, value });
    },
    [canEditData, handleUpdateCompany],
  );

  const handleFormChange = useCallback(
    ({ target: { name, type, value, checked } }) => {
      if (type === 'checkbox') {
        handleChange({ name, value: checked });

        return;
      }

      handleChange({ name, value });
    },
    [handleChange],
  );

  const handleInfoChange = useCallback(
    ({ target: { name, value } }) => {
      handleUpdateCompanyInfo({ [name]: value });
    },
    [handleUpdateCompanyInfo],
  );

  const handleErrorClose = useCallback(
    (event, field) => {
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
      <div className={classNames(style.host, 'tab-pane active')} id="slide-7">
        <CompanyBuyerCEO
          canEditData={canEditData}
          ceo={ceo}
          isResearcherUser={isResearcherUser}
          onActiveProjectsChange={handleActiveProjectsChange}
          onAddCEOProject={handleAddCEOProject}
          onChange={handleFormChange}
          onCurrentExecutiveLeadsChange={handleCurrentExecutiveLeadsChange}
          onDoubleClickRow={handleDoubleClickRow}
          onGetNextPageDataForActiveProjects={handleGetNextPageDataForActiveProjects}
          onGetNextPageDataForCurrentExecutiveLeads={handleGetNextPageDataForCurrentExecutiveLeads}
          onGetSuggestion={handleGetSuggestion}
          onInfoChange={handleInfoChange}
        />
      </div>
    </PrettyErrorProvider>
  );
};

CompanyBuyerCEOContainer.propTypes = {
  ceo: PropTypes.instanceOf(Map),
  createCEOProject: func.isRequired,
  getActiveProjects: func.isRequired,
  getCurrentExecutiveLeads: func.isRequired,
  handleGetHarvcoLeadSuggestion: func.isRequired,
  handleUpdateCompany: func.isRequired,
  isDuplicateCompany: PropTypes.bool.isRequired,
  saveCEOProject: func.isRequired,
  saveInfo: func.isRequired,
  updateCEO: func.isRequired,
};

export { CompanyBuyerCEOContainer };
export default connect(
  state => ({
    ceo: state.targetCompany.buyer.ceo,
    inputErrors: state.targetCompany.buyer.ceo.get('inputErrors'),
    isDuplicateCompany: state.targetCompany.mergeInfo.get('isDuplicateCompany'),
    user: state.auth.get('user', null),
  }),
  dispatch =>
    bindActionCreators(
      {
        getActiveProjects,
        getCurrentExecutiveLeads,
        updateCEO,
        createCEOProject,
        handleUpdateCompany,
        saveCEOProject,
        saveInfo,
        handleGetHarvcoLeadSuggestion,
        handleGetClientLeadSuggestion,
      },
      dispatch,
    ),
  mergeProps,
  connectOptions,
)(memo(CompanyBuyerCEOContainer));
